import React, { useState } from 'react';
import { ShieldCheck, CheckCircle, XCircle, Search, AlertOctagon, Info } from 'lucide-react';
import { format } from 'date-fns';
import { apiService } from '../services/apiService';

function AuditoriaCammesa() {
    const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [reporte, setReporte] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const generarReporte = async () => {
        setLoading(true);
        setError('');
        try {
            // El apiService usa el cliente axios base, por lo que agregamos un metodo ad-hoc aca
            // para no tocar apiService si no hace falta, o podemos usar fetch
            const API_URL = process.env.REACT_APP_API_URL || 'https://cammesa-servidor-770702622935.us-central1.run.app';
            const res = await fetch(`${API_URL}/api/reportes/disponibilidad?fecha=${selectedDate}`);
            if (!res.ok) throw new Error('Error al obtener reporte del servidor');
            
            const data = await res.json();
            setReporte(data);
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    };

    return (
        <div className="space-y-6">
            <div className="card">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center space-x-3">
                        <ShieldCheck className="w-8 h-8 text-blue-600" />
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Auditoría de Disponibilidad (CAMMESA)</h2>
                            <p className="text-sm text-gray-500">Herramienta legal para demostrar la continuidad de transmisión de la planta</p>
                        </div>
                    </div>

                    <div className="flex items-center space-x-4">
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            max={format(new Date(), 'yyyy-MM-dd')}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                        <button
                            onClick={generarReporte}
                            disabled={loading}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                        >
                            <Search className="w-5 h-5" />
                            <span>{loading ? 'Analizando BD...' : 'Generar Certificado'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4">
                    <p className="text-red-700">{error}</p>
                </div>
            )}

            {reporte && (
                <div className="space-y-6">
                    {/* Veredicto */}
                    <div className={`card ${reporte.resumen.disponibilidad_porcentaje >= 99 ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'} border-2`}>
                        <div className="flex items-start space-x-4">
                            {reporte.resumen.disponibilidad_porcentaje >= 99 ? (
                                <CheckCircle className="w-12 h-12 text-green-600 mt-1" />
                            ) : (
                                <AlertOctagon className="w-12 h-12 text-red-600 mt-1" />
                            )}
                            <div>
                                <h3 className="text-xl font-bold mb-2">
                                    Disponibilidad: {reporte.resumen.disponibilidad_porcentaje}%
                                </h3>
                                <p className="text-gray-800 text-lg">{reporte.veredicto}</p>
                            </div>
                        </div>
                    </div>

                    {/* Resumen Numérico */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="card">
                            <p className="text-sm text-gray-500">Registros en BD (Real)</p>
                            <p className="text-2xl font-bold text-blue-600">{reporte.resumen.total_registros}</p>
                        </div>
                        <div className="card">
                            <p className="text-sm text-gray-500">Registros Esperados</p>
                            <p className="text-2xl font-bold text-gray-600">{reporte.resumen.registros_esperados}</p>
                        </div>
                        <div className="card">
                            <p className="text-sm text-gray-500">Potencia Promedio</p>
                            <p className="text-2xl font-bold">{reporte.resumen.kw_promedio || 0} kW</p>
                        </div>
                        <div className="card">
                            <p className="text-sm text-gray-500">Gaps > 5 mins</p>
                            <p className={`text-2xl font-bold ${reporte.gaps.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {reporte.gaps.length}
                            </p>
                        </div>
                    </div>

                    {/* Arquitectura Info */}
                    <div className="card bg-gray-50 border border-gray-200">
                        <h4 className="font-bold flex items-center gap-2 mb-3">
                            <Info className="w-5 h-5 text-gray-500" />
                            Arquitectura y Responsabilidades
                        </h4>
                        <div className="text-sm text-gray-700 space-y-2">
                            <p><strong>Nota técnica:</strong> {reporte.arquitectura.nota}</p>
                            <p><strong>Ruta de datos:</strong> <code className="bg-gray-200 px-2 py-1 rounded">{reporte.arquitectura.flujo_cammesa}</code></p>
                            <div className="grid grid-cols-2 gap-4 mt-4">
                                <div className="bg-green-100 p-3 rounded">
                                    <p className="font-semibold text-green-800">Responsabilidad Planta</p>
                                    <p>{reporte.arquitectura.punto_responsabilidad_planta}</p>
                                </div>
                                <div className="bg-red-100 p-3 rounded">
                                    <p className="font-semibold text-red-800">Responsabilidad CAMMESA</p>
                                    <p>{reporte.arquitectura.punto_responsabilidad_cammesa}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabla de horas */}
                    <div className="card">
                        <h4 className="font-bold mb-4">Desglose Hora a Hora</h4>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-left text-sm">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="p-3 border-b">Hora</th>
                                        <th className="p-3 border-b text-right">Registros</th>
                                        <th className="p-3 border-b text-right">Potencia Promedio</th>
                                        <th className="p-3 border-b text-right">Máximo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reporte.horas.map(h => (
                                        <tr key={h.hora} className="border-b hover:bg-gray-50">
                                            <td className="p-3 font-semibold">{String(h.hora).padStart(2, '0')}:00</td>
                                            <td className="p-3 text-right">
                                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${h.registros >= 700 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {h.registros}
                                                </span>
                                            </td>
                                            <td className="p-3 text-right">{h.kw_promedio} kW</td>
                                            <td className="p-3 text-right text-gray-500">{h.kw_max} kW</td>
                                        </tr>
                                    ))}
                                    {reporte.horas.length === 0 && (
                                        <tr>
                                            <td colSpan="4" className="p-8 text-center text-gray-500">No hay datos registrados en esta fecha</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AuditoriaCammesa;
